import React, { useCallback, useRef, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Chip, Skeleton, Typography, Badge } from '@mui/material';
import { QrCode2, Download, Delete, ContentCopy, Folder as FolderIcon, GetApp } from '@mui/icons-material';
import FileIcon from '../common/FileIcon';
import { formatFileSize, formatDate } from '../../utils/formatters';

function ActionsCell({ file, onQRClick, onDelete, onDownload, onCopyLink }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%', height: '100%' }}>
      <Tooltip title={file.qr_downloaded ? "Scarica QR" : "Genera QR"}>
        <IconButton
          size="small"
          onClick={() => onQRClick(file)}
          sx={file.qr_downloaded ? {
            bgcolor: 'success.main',
            color: 'white',
            '&:hover': { bgcolor: 'success.dark' },
            width: 26,
            height: 26,
          } : { color: 'text.secondary' }}
        >
          {file.qr_downloaded ? (
            <Badge
              badgeContent={<GetApp sx={{ fontSize: 8 }} />}
              sx={{ '& .MuiBadge-badge': { bgcolor: 'white', color: 'success.main', minWidth: 12, height: 12, p: 0 } }}
            >
              <QrCode2 sx={{ fontSize: 16 }} />
            </Badge>
          ) : (
            <QrCode2 fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Copia link">
        <IconButton size="small" onClick={() => onCopyLink(file)}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download">
        <IconButton size="small" onClick={() => onDownload(file)}>
          <Download fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={file.qr_downloaded ? "Eliminazione bloccata (QR scaricato)" : "Elimina"}>
        <span>
        <IconButton size="small" color="error" disabled={file.qr_downloaded} onClick={() => onDelete(file)}>
          <Delete fontSize="small" />
        </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default function FileTable({
  files,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  onQRClick,
  onDelete,
  onDownload,
  onCopyLink,
  onContextMenu,
  ordering,
  onOrderingChange,
  selectedIds,
  onSelectionChange,
  search,
  onRowClick,
}) {
  const sentinelRef = useRef(null);

  // IntersectionObserver for infinite scroll (DataGrid community doesn't have onRowsScrollEnd)
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const columns = [
    {
      field: 'original_filename',
      headerName: 'Nome',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <FileIcon mimeType={params.row.mime_type} size="small" />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.value}
          </span>
        </Box>
      ),
    },
    {
      field: 'file_size',
      headerName: 'Dimensione',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => formatFileSize(params.value),
    },
    {
      field: 'download_count',
      headerName: 'Download',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" color="primary" />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Data',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    ...(search
      ? [
          {
            field: 'folder_name',
            headerName: 'Cartella',
            width: 160,
            headerAlign: 'left',
            align: 'left',
            sortable: false,
            renderCell: (params) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FolderIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="body2" noWrap>
                  {params.value || 'Root'}
                </Typography>
              </Box>
            ),
          },
        ]
      : []),
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 180,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <ActionsCell
          file={params.row}
          onQRClick={onQRClick}
          onDelete={onDelete}
          onDownload={onDownload}
          onCopyLink={onCopyLink}
        />
      ),
    },
  ];

  const handleSortModelChange = useCallback(
    (model) => {
      if (model.length === 0) {
        onOrderingChange('-created_at');
        return;
      }
      const { field, sort } = model[0];
      onOrderingChange(sort === 'desc' ? `-${field}` : field);
    },
    [onOrderingChange]
  );

  // Parse current ordering into DataGrid sortModel
  const sortModel = [];
  if (ordering) {
    const desc = ordering.startsWith('-');
    const field = desc ? ordering.slice(1) : ordering;
    sortModel.push({ field, sort: desc ? 'desc' : 'asc' });
  }

  const rowSelectionModel = selectedIds ? Array.from(selectedIds) : [];

  const handleRowSelectionModelChange = useCallback(
    (model) => {
      if (onSelectionChange) {
        onSelectionChange(new Set(model));
      }
    },
    [onSelectionChange]
  );

  if (isLoading) {
    return (
      <Box>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height={52} sx={{ my: 0.5 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <DataGrid
        rows={files}
        columns={columns}
        getRowId={(row) => row.id}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        sortingMode="server"
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        hideFooter
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        autoHeight
        onRowClick={(params, event) => {
          // Don't open preview if clicking on action buttons or checkbox
          if (event.target.closest('button')) return;
          if (event.target.closest('.MuiCheckbox-root')) return;
          if (onRowClick) onRowClick(params.row);
        }}
        slotProps={{
          row: {
            draggable: true,
            style: { cursor: 'pointer' },
            onDragStart: (e) => {
              const rowId = e.currentTarget.getAttribute('data-id');
              if (rowId) {
                e.dataTransfer.setData('application/x-file-id', rowId);
                e.dataTransfer.effectAllowed = 'move';
              }
            },
            onContextMenu: (e) => {
              e.preventDefault();
              if (!onContextMenu) return;
              const rowId = e.currentTarget.getAttribute('data-id');
              const file = files.find((f) => f.id === rowId);
              if (file) onContextMenu(e, file);
            },
          },
        }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'grey.50',
            borderRadius: 1,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderColor: 'grey.100',
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: 'grey.50',
          },
        }}
      />
      {/* Sentinel for infinite scroll */}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {isFetchingNextPage && (
        <Box sx={{ py: 2 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={52} sx={{ my: 0.5 }} />
          ))}
        </Box>
      )}
    </Box>
  );
}
